// Localized SEO copy for the 14 key routes (title / meta description / H1).
// Source: owner-approved SEO copy deck 2026-10-24. FR/DE/SV/AR and ES are
// pending native-speaker review — do not extend beyond these routes.

import type { LanguageCode } from "./config";

export type SeoRouteKey =
  | "home"
  | "smartToilets"
  | "rem7"
  | "collection"
  | "business"
  | "bathSanitary"
  | "smartToiletCost"
  | "tradeBrochure"
  | "compare"
  | "faq"
  | "about"
  | "contact"
  | "marketsSpain"
  | "blog";

export interface SeoCopy {
  title: string;
  description: string;
  h1: string;
}

/** Language-agnostic path suffix per route ("" = locale home). */
export const seoPaths: Record<SeoRouteKey, string> = {
  home: "",
  smartToilets: "/smart-toilets",
  rem7: "/products/rem7",
  collection: "/collection",
  business: "/business",
  bathSanitary: "/bath-sanitary",
  smartToiletCost: "/smart-toilet-cost",
  tradeBrochure: "/trade-brochure",
  compare: "/compare",
  faq: "/faq",
  about: "/about",
  contact: "/contact",
  marketsSpain: "/markets/spain",
  blog: "/blog",
};

export const seoCopy: Record<SeoRouteKey, Record<LanguageCode, SeoCopy>> = {
  home: {
    en: {
      title: "REM7 Smart Toilets Spain | Aureum VII Trading Marbella",
      description:
        "REM7 smart toilets and the Aureum VII bathroom collection, supplied from Marbella to homeowners, hotels, developers and architects across Spain.",
      h1: "REM7 Smart Toilets & the Aureum VII Collection",
    },
    es: {
      title: "Inodoros inteligentes REM7 en España | Aureum VII Marbella",
      description:
        "Inodoros inteligentes REM7 y colección de baño Aureum VII desde Marbella para particulares, hoteles, promotoras y arquitectos en España.",
      h1: "Inodoros inteligentes REM7 y la colección Aureum VII",
    },
    fr: {
      title: "WC japonais REM7 en Espagne | Aureum VII Trading",
      description:
        "WC japonais REM7 et collection de salle de bain Aureum VII, depuis Marbella pour particuliers, hôtels, promoteurs et architectes en Espagne.",
      h1: "WC japonais REM7 et la collection Aureum VII",
    },
    de: {
      title: "REM7 Dusch-WC in Spanien | Aureum VII Trading Marbella",
      description:
        "REM7 Dusch-WCs und die Aureum VII Badkollektion, aus Marbella für Eigentümer, Hotels, Bauträger und Architekten in ganz Spanien.",
      h1: "REM7 Dusch-WCs & die Aureum VII Kollektion",
    },
    sv: {
      title: "REM7 duschtoalett i Spanien | Aureum VII Trading",
      description:
        "REM7 duschtoaletter och Aureum VII badrumskollektion, från Marbella till husägare, hotell, byggherrar och arkitekter i hela Spanien.",
      h1: "REM7 duschtoaletter och Aureum VII-kollektionen",
    },
    ar: {
      title: "مرحاض ذكي REM7 في إسبانيا | Aureum VII Trading",
      description:
        "مراحيض ذكية REM7 ومجموعة حمامات Aureum VII من ماربيا لأصحاب المنازل والفنادق والمطورين العقاريين والمهندسين المعماريين في إسبانيا.",
      h1: "مراحيض REM7 الذكية ومجموعة Aureum VII",
    },
  },
  smartToilets: {
    en: {
      title: "Smart Toilets & Japanese Toilets in Spain | Aureum VII",
      description:
        "Browse the Aureum VII smart toilet range, including REM7. Compare models and request trade or project pricing from our Marbella team.",
      h1: "Smart Toilets",
    },
    es: {
      title: "Inodoros inteligentes y váter japonés | Aureum VII",
      description:
        "Descubra la gama de inodoros inteligentes Aureum VII, incluido REM7. Compare modelos y solicite precio profesional a nuestro equipo en Marbella.",
      h1: "Inodoros inteligentes",
    },
    fr: {
      title: "WC japonais et WC lavants | Aureum VII",
      description:
        "Découvrez la gamme de WC japonais Aureum VII, dont le REM7. Comparez les modèles et demandez un tarif professionnel à notre équipe de Marbella.",
      h1: "WC japonais et WC lavants",
    },
    de: {
      title: "Dusch-WC & japanische Toilette | Aureum VII",
      description:
        "Entdecken Sie die Dusch-WC-Serie von Aureum VII inkl. REM7. Modelle vergleichen und Projektpreise bei unserem Team in Marbella anfragen.",
      h1: "Dusch-WCs",
    },
    sv: {
      title: "Duschtoalett & japansk toalett | Aureum VII",
      description:
        "Se Aureum VII:s duschtoaletter, inklusive REM7. Jämför modeller och begär proffs- eller projektpris från vårt team i Marbella.",
      h1: "Duschtoaletter",
    },
    ar: {
      title: "مراحيض ذكية وكرسي حمام ذكي | Aureum VII",
      description:
        "تصفح مجموعة المراحيض الذكية من Aureum VII بما فيها REM7. قارن الموديلات واطلب أسعار المشاريع من فريقنا في ماربيا.",
      h1: "المراحيض الذكية",
    },
  },
  rem7: {
    en: {
      title: "REM7 Smart Toilet | Aureum VII Trading",
      description:
        "REM7 smart toilet from Aureum VII Trading. See features and request specifications or project pricing from our Marbella team.",
      h1: "REM7 Smart Toilet",
    },
    es: {
      title: "Inodoro inteligente REM7 | Aureum VII Trading",
      description:
        "Inodoro inteligente REM7 de Aureum VII Trading. Consulte sus funciones y solicite fichas técnicas o precio para su proyecto en Marbella.",
      h1: "Inodoro inteligente REM7",
    },
    fr: {
      title: "WC japonais REM7 | Aureum VII Trading",
      description:
        "WC japonais REM7 d'Aureum VII Trading. Découvrez ses fonctions et demandez fiches techniques ou tarif projet à notre équipe de Marbella.",
      h1: "WC japonais REM7",
    },
    de: {
      title: "REM7 Dusch-WC | Aureum VII Trading",
      description:
        "REM7 Dusch-WC von Aureum VII Trading. Funktionen ansehen und Datenblätter oder Projektpreise bei unserem Team in Marbella anfragen.",
      h1: "REM7 Dusch-WC",
    },
    sv: {
      title: "REM7 duschtoalett | Aureum VII Trading",
      description:
        "REM7 duschtoalett från Aureum VII Trading. Se funktionerna och begär produktblad eller projektpris från vårt team i Marbella.",
      h1: "REM7 duschtoalett",
    },
    ar: {
      title: "مرحاض ذكي REM7 | Aureum VII Trading",
      description:
        "مرحاض REM7 الذكي من Aureum VII Trading. تعرّف على مزاياه واطلب المواصفات الفنية أو سعر مشروعك من فريقنا في ماربيا.",
      h1: "مرحاض REM7 الذكي",
    },
  },
  collection: {
    en: {
      title: "The Aureum VII Collection | Luxury Smart Bathroom",
      description:
        "Explore the Aureum VII Collection of smart toilets and bathroom pieces for villas, hotels and residential developments in Spain.",
      h1: "The Aureum VII Collection",
    },
    es: {
      title: "La colección Aureum VII | Baño de lujo inteligente",
      description:
        "Explore la colección Aureum VII de inodoros inteligentes y piezas de baño para villas, hoteles y promociones residenciales en España.",
      h1: "La colección Aureum VII",
    },
    fr: {
      title: "La collection Aureum VII | Salle de bain de luxe",
      description:
        "Découvrez la collection Aureum VII de WC japonais et d'éléments de salle de bain pour villas, hôtels et programmes résidentiels en Espagne.",
      h1: "La collection Aureum VII",
    },
    de: {
      title: "Die Aureum VII Kollektion | Luxus-Badezimmer",
      description:
        "Die Aureum VII Kollektion: Dusch-WCs und Badobjekte für Villen, Hotels und Wohnbauprojekte in Spanien.",
      h1: "Die Aureum VII Kollektion",
    },
    sv: {
      title: "Aureum VII-kollektionen | Lyxigt badrum",
      description:
        "Upptäck Aureum VII-kollektionen med duschtoaletter och badrumsprodukter för villor, hotell och bostadsprojekt i Spanien.",
      h1: "Aureum VII-kollektionen",
    },
    ar: {
      title: "مجموعة Aureum VII | حمامات فاخرة ذكية",
      description:
        "اكتشف مجموعة Aureum VII من المراحيض الذكية وقطع الحمامات للفلل والفنادق والمشاريع السكنية في إسبانيا.",
      h1: "مجموعة Aureum VII",
    },
  },
  business: {
    en: {
      title: "Smart Toilets for Hotels & Developers | Aureum VII",
      description:
        "Trade supply of smart toilets for hotels, developers, builders and architects in Spain, €600 per unit. Request project pricing and spec sheets.",
      h1: "Smart Toilets for Hotels, Developers & Architects",
    },
    es: {
      title: "Inodoros inteligentes para hoteles y promotoras | Aureum",
      description:
        "Suministro profesional de inodoros inteligentes para hoteles, promotoras y arquitectos en España, 600 € por unidad. Pida fichas técnicas.",
      h1: "Inodoros inteligentes para hoteles, promotoras y arquitectos",
    },
    fr: {
      title: "WC japonais pour hôtels et promoteurs | Aureum VII",
      description:
        "Fourniture professionnelle de WC japonais pour hôtels, promoteurs et architectes en Espagne, 600 € l'unité. Demandez les fiches.",
      h1: "WC japonais pour hôtels, promoteurs et architectes",
    },
    de: {
      title: "Dusch-WCs für Hotels & Bauträger | Aureum VII",
      description:
        "Objektlieferung von Dusch-WCs für Hotels, Bauträger und Architekten in Spanien, 600 € pro Stück. Projektpreise und Datenblätter anfragen.",
      h1: "Dusch-WCs für Hotels, Bauträger & Architekten",
    },
    sv: {
      title: "Duschtoaletter för hotell & byggherrar | Aureum VII",
      description:
        "Proffsleveranser av duschtoaletter till hotell, byggherrar och arkitekter i Spanien, 600 € per enhet. Begär projektpris och produktblad.",
      h1: "Duschtoaletter för hotell, byggherrar och arkitekter",
    },
    ar: {
      title: "مراحيض ذكية للفنادق والمطورين | Aureum VII",
      description:
        "توريد مراحيض ذكية للفنادق والمطورين العقاريين والمعماريين في إسبانيا، 600 يورو للوحدة. اطلب أسعار المشروع والمواصفات.",
      h1: "مراحيض ذكية للفنادق والمطورين والمهندسين المعماريين",
    },
  },
  bathSanitary: {
    en: {
      title: "Luxury Bathtubs & Basins | Aureum VII Bath & Sanitary",
      description:
        "REM7 whirlpool baths, Aria and Vela bathtubs and Vena basins and vanities from Aureum VII Trading, Marbella.",
      h1: "Bath & Sanitary",
    },
    es: {
      title: "Bañeras y lavabos de lujo | Baño y sanitarios Aureum VII",
      description:
        "Bañeras de hidromasaje REM7, bañeras Aria y Vela, y lavabos y muebles Vena de Aureum VII Trading, Marbella.",
      h1: "Baño y sanitarios",
    },
    fr: {
      title: "Baignoires et vasques de luxe | Aureum VII",
      description:
        "Baignoires balnéo REM7, baignoires Aria et Vela, vasques et meubles Vena d'Aureum VII Trading, Marbella.",
      h1: "Bain et sanitaire",
    },
    de: {
      title: "Luxus-Badewannen & Waschtische | Aureum VII",
      description:
        "REM7 Whirlpool-Wannen, Aria- und Vela-Badewannen sowie Vena Waschtische und Möbel von Aureum VII Trading, Marbella.",
      h1: "Bad & Sanitär",
    },
    sv: {
      title: "Lyxiga badkar & handfat | Aureum VII",
      description:
        "REM7 bubbelbadkar, Aria- och Vela-badkar samt Vena handfat och badrumsmöbler från Aureum VII Trading, Marbella.",
      h1: "Bad & sanitet",
    },
    ar: {
      title: "أحواض استحمام ومغاسل فاخرة | Aureum VII",
      description:
        "أحواض جاكوزي REM7 وأحواض استحمام Aria وVela ومغاسل وخزائن Vena من Aureum VII Trading في ماربيا.",
      h1: "الحمامات والأدوات الصحية",
    },
  },
  smartToiletCost: {
    en: {
      title: "Smart Toilet Cost in Spain | Aureum VII Pricing Guide",
      description:
        "What affects smart toilet cost in Spain: model, installation and project size. Trade supply €600 per unit. Request a quote from Aureum VII.",
      h1: "Smart Toilet Cost in Spain",
    },
    es: {
      title: "Precio de un inodoro inteligente en España | Aureum VII",
      description:
        "Qué influye en el precio de un inodoro inteligente: modelo, instalación y proyecto. Suministro profesional 600 € por unidad. Pida presupuesto.",
      h1: "¿Cuánto cuesta un inodoro inteligente en España?",
    },
    fr: {
      title: "Prix d'un WC japonais en Espagne | Aureum VII",
      description:
        "Ce qui influe sur le prix d'un WC japonais : modèle, installation, projet. Fourniture pro 600 € l'unité. Demandez un devis.",
      h1: "Combien coûte un WC japonais en Espagne ?",
    },
    de: {
      title: "Dusch-WC Preis in Spanien | Aureum VII Ratgeber",
      description:
        "Was den Preis eines Dusch-WCs bestimmt: Modell, Installation, Projektgröße. Objektlieferung 600 € pro Stück. Angebot anfragen.",
      h1: "Was kostet ein Dusch-WC in Spanien?",
    },
    sv: {
      title: "Duschtoalett pris i Spanien | Aureum VII prisguide",
      description:
        "Vad påverkar priset på en duschtoalett: modell, installation och projekt. Proffsleveranser 600 € per enhet. Begär offert.",
      h1: "Vad kostar en duschtoalett i Spanien?",
    },
    ar: {
      title: "سعر المرحاض الذكي في إسبانيا | دليل Aureum VII",
      description:
        "ما الذي يحدد سعر المرحاض الذكي: الموديل والتركيب وحجم المشروع. توريد للشركات 600 يورو للوحدة. اطلب عرض سعر.",
      h1: "كم يكلف المرحاض الذكي في إسبانيا؟",
    },
  },
  tradeBrochure: {
    en: {
      title: "Trade Brochure for Architects & Developers | Aureum VII",
      description:
        "Download the Aureum VII trade brochure: smart toilets and bathroom pieces for architects, developers, builders and hotels in Spain.",
      h1: "Trade Brochure",
    },
    es: {
      title: "Catálogo para arquitectos y promotoras | Aureum VII",
      description:
        "Descargue el catálogo profesional Aureum VII: inodoros inteligentes y piezas de baño para arquitectos, promotoras, constructoras y hoteles.",
      h1: "Catálogo profesional",
    },
    fr: {
      title: "Brochure pro pour architectes et promoteurs | Aureum VII",
      description:
        "Téléchargez la brochure professionnelle Aureum VII : WC japonais et éléments de salle de bain pour architectes, promoteurs et hôtels.",
      h1: "Brochure professionnelle",
    },
    de: {
      title: "Objektbroschüre für Architekten | Aureum VII",
      description:
        "Laden Sie die Aureum VII Objektbroschüre: Dusch-WCs und Badobjekte für Architekten, Bauträger, Bauunternehmen und Hotels in Spanien.",
      h1: "Objektbroschüre",
    },
    sv: {
      title: "Broschyr för arkitekter & byggherrar | Aureum VII",
      description:
        "Ladda ner Aureum VII:s proffsbroschyr: duschtoaletter och badrumsprodukter för arkitekter, byggherrar, byggföretag och hotell.",
      h1: "Proffsbroschyr",
    },
    ar: {
      title: "كتيّب للمعماريين والمطورين | Aureum VII",
      description:
        "حمّل كتيّب Aureum VII للمحترفين: مراحيض ذكية وقطع حمامات للمهندسين المعماريين والمطورين وشركات البناء والفنادق في إسبانيا.",
      h1: "الكتيّب التجاري",
    },
  },
  compare: {
    en: {
      title: "Compare Smart Toilets | Aureum VII Model Comparison",
      description:
        "Compare Aureum VII smart toilet models side by side to choose the right one for your home, hotel or development.",
      h1: "Compare Smart Toilets",
    },
    es: {
      title: "Comparar inodoros inteligentes | Comparativa Aureum VII",
      description:
        "Compare los modelos de inodoro inteligente Aureum VII para elegir el adecuado para su vivienda, hotel o promoción.",
      h1: "Comparar inodoros inteligentes",
    },
    fr: {
      title: "Comparer les WC japonais | Comparatif Aureum VII",
      description:
        "Comparez les modèles de WC japonais Aureum VII pour choisir celui qui convient à votre maison, hôtel ou programme.",
      h1: "Comparer les WC japonais",
    },
    de: {
      title: "Dusch-WC Vergleich | Aureum VII Modelle",
      description:
        "Vergleichen Sie die Dusch-WC-Modelle von Aureum VII und wählen Sie das passende für Ihr Zuhause, Hotel oder Bauprojekt.",
      h1: "Dusch-WCs vergleichen",
    },
    sv: {
      title: "Jämför duschtoaletter | Aureum VII modeller",
      description:
        "Jämför Aureum VII:s duschtoaletter sida vid sida och välj rätt modell för ditt hem, hotell eller projekt.",
      h1: "Jämför duschtoaletter",
    },
    ar: {
      title: "مقارنة المراحيض الذكية | موديلات Aureum VII",
      description:
        "قارن موديلات المراحيض الذكية من Aureum VII جنباً إلى جنب لاختيار الأنسب لمنزلك أو فندقك أو مشروعك.",
      h1: "مقارنة المراحيض الذكية",
    },
  },
  faq: {
    en: {
      title: "Smart Toilet FAQ | Aureum VII Trading",
      description:
        "Answers to common questions about Aureum VII smart toilets: installation, delivery, warranty and trade orders in Spain.",
      h1: "Frequently Asked Questions",
    },
    es: {
      title: "Preguntas frecuentes sobre inodoros inteligentes | Aureum",
      description:
        "Respuestas a las preguntas más comunes sobre los inodoros inteligentes Aureum VII: instalación, envío, garantía y pedidos profesionales.",
      h1: "Preguntas frecuentes",
    },
    fr: {
      title: "FAQ WC japonais et WC lavants | Aureum VII",
      description:
        "Réponses aux questions fréquentes sur les WC japonais Aureum VII : installation, livraison, garantie et commandes professionnelles en Espagne.",
      h1: "Questions fréquentes",
    },
    de: {
      title: "Dusch-WC FAQ | Aureum VII Trading",
      description:
        "Antworten auf häufige Fragen zu Aureum VII Dusch-WCs: Installation, Lieferung, Garantie und Objektbestellungen in Spanien.",
      h1: "Häufige Fragen",
    },
    sv: {
      title: "Vanliga frågor om duschtoaletter | Aureum VII",
      description:
        "Svar på vanliga frågor om Aureum VII:s duschtoaletter: installation, leverans, garanti och proffsbeställningar i Spanien.",
      h1: "Vanliga frågor",
    },
    ar: {
      title: "أسئلة شائعة عن المراحيض الذكية | Aureum VII",
      description:
        "إجابات عن الأسئلة الشائعة حول مراحيض Aureum VII الذكية: التركيب والتوصيل والضمان وطلبات الشركات في إسبانيا.",
      h1: "الأسئلة الشائعة",
    },
  },
  about: {
    en: {
      title: "About Aureum VII Trading | Smart Toilets, Marbella",
      description:
        "Aureum VII Trading is a Marbella-based supplier of smart toilets and bathroom pieces for homeowners and trade clients in Spain.",
      h1: "About Aureum VII Trading",
    },
    es: {
      title: "Sobre Aureum VII Trading | Inodoros inteligentes, Marbella",
      description:
        "Aureum VII Trading es un proveedor de inodoros inteligentes y piezas de baño con sede en Marbella para particulares y profesionales.",
      h1: "Sobre Aureum VII Trading",
    },
    fr: {
      title: "À propos d'Aureum VII Trading | Marbella, Espagne",
      description:
        "Aureum VII Trading est un fournisseur de WC japonais et d'éléments de salle de bain basé à Marbella, pour particuliers et professionnels.",
      h1: "À propos d'Aureum VII Trading",
    },
    de: {
      title: "Über Aureum VII Trading | Dusch-WCs aus Marbella",
      description:
        "Aureum VII Trading ist ein Anbieter von Dusch-WCs und Badobjekten mit Sitz in Marbella, für Privat- und Geschäftskunden in Spanien.",
      h1: "Über Aureum VII Trading",
    },
    sv: {
      title: "Om Aureum VII Trading | Duschtoaletter, Marbella",
      description:
        "Aureum VII Trading är en leverantör av duschtoaletter och badrumsprodukter i Marbella, för privatkunder och företag i Spanien.",
      h1: "Om Aureum VII Trading",
    },
    ar: {
      title: "من نحن | Aureum VII Trading، ماربيا",
      description:
        "Aureum VII Trading مورّد للمراحيض الذكية وقطع الحمامات مقره ماربيا، يخدم الأفراد والشركات في إسبانيا.",
      h1: "عن Aureum VII Trading",
    },
  },
  contact: {
    en: {
      title: "Contact Aureum VII Trading | Marbella, Spain",
      description:
        "Contact our Marbella team for product information, trade enquiries and project discussions. Call +34 688 495 309 or email us.",
      h1: "Contact Us",
    },
    es: {
      title: "Contacto | Aureum VII Trading, Marbella",
      description:
        "Contacte con nuestro equipo en Marbella para información, consultas profesionales y proyectos. Llame al +34 688 495 309 o escríbanos.",
      h1: "Contacto",
    },
    fr: {
      title: "Contact | Aureum VII Trading, Marbella",
      description:
        "Contactez notre équipe de Marbella pour vos questions produit, demandes pro et projets. Appelez le +34 688 495 309 ou écrivez-nous.",
      h1: "Contact",
    },
    de: {
      title: "Kontakt | Aureum VII Trading, Marbella",
      description:
        "Kontaktieren Sie unser Team in Marbella für Produktinfos, Objektanfragen und Projekte. Rufen Sie +34 688 495 309 an oder schreiben Sie uns.",
      h1: "Kontakt",
    },
    sv: {
      title: "Kontakt | Aureum VII Trading, Marbella",
      description:
        "Kontakta vårt team i Marbella om produkter, proffsförfrågningar och projekt. Ring +34 688 495 309 eller mejla oss.",
      h1: "Kontakt",
    },
    ar: {
      title: "اتصل بنا | Aureum VII Trading، ماربيا",
      description:
        "تواصل مع فريقنا في ماربيا للاستفسار عن المنتجات وطلبات الشركات والمشاريع. اتصل على +34 688 495 309 أو راسلنا.",
      h1: "اتصل بنا",
    },
  },
  marketsSpain: {
    en: {
      title: "Smart Toilets in Spain | Aureum VII, Marbella",
      description:
        "Aureum VII supplies smart toilets and bathroom pieces across Spain from Marbella, for private homes, hotels and developments.",
      h1: "Smart Toilets Across Spain",
    },
    es: {
      title: "Inodoros inteligentes en España | Aureum VII, Marbella",
      description:
        "Aureum VII suministra inodoros inteligentes y piezas de baño en toda España desde Marbella, para viviendas, hoteles y promociones.",
      h1: "Inodoros inteligentes en toda España",
    },
    fr: {
      title: "WC japonais en Espagne | Aureum VII, Marbella",
      description:
        "Aureum VII fournit WC japonais et éléments de salle de bain dans toute l'Espagne depuis Marbella, pour maisons, hôtels et programmes.",
      h1: "WC japonais dans toute l'Espagne",
    },
    de: {
      title: "Dusch-WCs in Spanien | Aureum VII, Marbella",
      description:
        "Aureum VII liefert Dusch-WCs und Badobjekte in ganz Spanien aus Marbella, für Privathäuser, Hotels und Wohnbauprojekte.",
      h1: "Dusch-WCs in ganz Spanien",
    },
    sv: {
      title: "Duschtoaletter i Spanien | Aureum VII, Marbella",
      description:
        "Aureum VII levererar duschtoaletter och badrumsprodukter i hela Spanien från Marbella, till villor, hotell och bostadsprojekt.",
      h1: "Duschtoaletter i hela Spanien",
    },
    ar: {
      title: "مراحيض ذكية في إسبانيا | Aureum VII، ماربيا",
      description:
        "توفر Aureum VII المراحيض الذكية وقطع الحمامات في جميع أنحاء إسبانيا من ماربيا، للمنازل والفنادق والمشاريع السكنية.",
      h1: "مراحيض ذكية في جميع أنحاء إسبانيا",
    },
  },
  blog: {
    en: {
      title: "Smart Toilet Guides & Advice | Aureum VII Blog",
      description:
        "Guides, comparisons and specification advice on smart toilets for homeowners, hotels, architects and developers.",
      h1: "Aureum VII Blog",
    },
    es: {
      title: "Blog Aureum VII",
      description:
        "Guías, comparativas y consejos de especificación sobre inodoros inteligentes para particulares, hoteles, arquitectos y promotoras.",
      h1: "Blog Aureum VII",
    },
    fr: {
      title: "Blog Aureum VII",
      description:
        "Guides, comparatifs et conseils de prescription sur les WC japonais pour particuliers, hôtels, architectes et promoteurs.",
      h1: "Blog Aureum VII",
    },
    de: {
      title: "Aureum VII Blog",
      description:
        "Ratgeber, Vergleiche und Planungstipps zu Dusch-WCs für Eigentümer, Hotels, Architekten und Bauträger.",
      h1: "Aureum VII Blog",
    },
    sv: {
      title: "Aureum VII blogg",
      description:
        "Guider, jämförelser och råd om duschtoaletter för husägare, hotell, arkitekter och byggherrar.",
      h1: "Aureum VII blogg",
    },
    ar: {
      title: "مدونة Aureum VII",
      description:
        "أدلة ومقارنات ونصائح فنية حول المراحيض الذكية لأصحاب المنازل والفنادق والمهندسين المعماريين والمطورين.",
      h1: "مدونة Aureum VII",
    },
  },
};

/** Localized copy for one route, falling back to English when missing. */
export const getSeoCopy = (route: SeoRouteKey, language: LanguageCode): SeoCopy =>
  seoCopy[route][language] ?? seoCopy[route].en;
